import React, { useState } from 'react';
import { Button, Card, Alert, notification } from 'antd';
import { observer } from 'mobx-react';
import UserStore from '@/stores/UserStore';

export interface VerifyUserProps {
    onResendVerification: () => Promise<void> | void;
    onSuccess: () => void;
}

const VerifyUser: React.FC<VerifyUserProps> = ({ onResendVerification, onSuccess }) => {
    const [resentVisible, setResentVisible] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false);

    const email = UserStore.user?.email ?? '';

    const handleResend = async () => {
        try {
            setResendLoading(true);
            await onResendVerification();

            setResentVisible(true);

            // Auto-hide success message after 5 seconds
            setTimeout(() => {
                setResentVisible(false);
            }, 10000);
        } catch {
            notification.error({
                message: 'Error',
                description: 'Unable to resend verification email.',
                key: 'refresh_verification_error',
            });
        } finally {
            setResendLoading(false);
        }
    };

    const handleRefreshVerification = async () => {
        try {
            setRefreshLoading(true);

            await UserStore.pullUser();

            if (UserStore.isVerified) {
                onSuccess();
            } else {
                notification.warning({
                    message: 'Still Not Verified',
                    description: 'Please check your inbox and click the verification link before continuing.',
                    key: 'refresh_verification_error',
                });
            }
        } catch {
            notification.error({
                message: 'Error',
                description: 'Unable to refresh verification status.',
                key: 'refresh_verification_error',
            });
        } finally {
            setRefreshLoading(false);
        }
    };

    return (
        <div className="container py-4">
            <Card className="verify-card text-center mx-auto max-w-md" bordered={false}>
                {resentVisible && (
                    <Alert
                        type="success"
                        message={`A new verification link has been sent to ${email}.`}
                        showIcon
                        closable
                        className="mb-4 text-left"
                    />
                )}

                <p className="text-gray-700">
                    We’ve sent a verification email to <strong>{email}</strong>.
                    <br />
                    Please verify your email and then press the Verify and Continue button below.
                </p>

                <div className="flex flex-col gap-4 mt-6">
                    {/* PRIMARY ACTION */}
                    <Button type="primary" size="large" loading={refreshLoading} onClick={handleRefreshVerification}>
                        Verify and Continue
                    </Button>

                    {/* SECONDARY ACTION */}
                    <Button type="link" loading={resendLoading} onClick={handleResend}>
                        Didn’t receive it? Resend Email
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default observer(VerifyUser);
